import { DashboardOutlined, HomeWorkOutlined } from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Skeleton } from '@mui/material';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import useGetAvatarsV2 from '@/hooks/useGetAvatarsV2';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    CONTACT_EMAIL_AVATAR_PROPERTY,
    CONTACT_NAME_AVATAR_PROPERTY,
    CONTACT_PHONE_AVATAR_PROPERTY,
    NAVIGATION_ROUTE,
} from '@/utils/CONST';
import { toShortFormatDate } from '@/utils/date';
import { getAvatarPropertyValue } from '@/utils/getProperty';
import extractIdFromURI from '@/utils/id';
import CollapseMenu from '@/components/common/collapse-menu/collapse-menu';
import DashboardsInfoBox from '@/components/common/dashboards-info-box/dashboards-info-box';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import InfoTwoLinesBlock from '@/components/common/info-two-lines-block/info-two-lines-block';

interface AvatarInfoDialogProps {
    avatarId: string | null;
    onClose: () => void;
}

const AvatarInfoDialog = ({ avatarId, onClose }: AvatarInfoDialogProps) => {
    const translate = useTranslate();
    const navigate = useNavigate();

    const avatarUriId = avatarId ? composeUri(AVATARS, avatarId) : null;

    const { firstAvatar: avatar, isLoading: isLoadingAvatar } = useGetAvatarsV2({
        filters: {
            id: avatarUriId,
            serialization_groups: ['read_avatar_current_node', 'read_node', 'read_avatar_root', 'read_avatar_parent'],
        },
        enabled: !!avatarUriId,
    });

    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties(
        avatarUriId
            ? {
                  avatarId: avatarUriId,
                  propertiesNames: [
                      CONTACT_NAME_AVATAR_PROPERTY,
                      CONTACT_PHONE_AVATAR_PROPERTY,
                      CONTACT_EMAIL_AVATAR_PROPERTY,
                  ],
              }
            : { avatarId: undefined, propertiesNames: [] },
    );

    // Get site with its parent (client) loaded - using useGetAvatarsV2 and taking the first avatar
    // In API v2, parent is always a direct URI string
    const siteUriId = avatar?.parent || null;

    const { firstAvatar: site, isLoading: isLoadingSite } = useGetAvatarsV2({
        filters: {
            id: siteUriId,
            serialization_groups: ['read_avatar_current_node', 'read_node', 'read_avatar_root', 'read_avatar_parent'],
        },
        enabled: !!siteUriId,
    });

    const isLoading = isLoadingAvatar || isLoadingProperties || isLoadingSite;

    const handleNavigateToPlaceInfo = () => {
        if (!avatarId || !avatar || isLoading) return;
        // Get siteId from avatar parent (in API v2, parent is always a direct URI string)
        const siteId = avatar.parent ? extractIdFromURI(avatar.parent as unknown as string) : null;
        // Get clientId from site parent (in API v2, parent is always a direct URI string)
        const clientId = site?.parent ? extractIdFromURI(site.parent as unknown as string) : null;

        if (siteId && clientId) {
            navigate(`/${NAVIGATION_ROUTE}/client/${clientId}/site/${siteId}/place/${avatarId}`);
        }
    };

    if (!avatarId) {
        return null;
    }

    return (
        <DialogTemplate
            title={isLoading ? '' : avatar?.name}
            open
            onClose={onClose}
            maxWidth='lg'
            titleActions={
                <IconButton onClick={handleNavigateToPlaceInfo} color='primary' disabled={isLoading}>
                    <OpenInNewIcon />
                </IconButton>
            }
        >
            {isLoading ? (
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        p: 2,
                    }}
                >
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} height={40} />
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        alignContent: 'start',
                        alignItems: 'start',
                        gap: 2,
                    }}
                >
                    <CollapseMenu
                        icon={<HomeWorkOutlined />}
                        text={translate('map.dialog.general_information')}
                        defaultOpen
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 1,
                                p: 1,
                            }}
                        >
                            <InfoTwoLinesBlock
                                title={translate('site.form.address')}
                                subtitle={site?.addressText ?? ''}
                                id='address'
                            />

                            <InfoTwoLinesBlock
                                title={translate('site.form.city')}
                                subtitle={site?.city ?? ''}
                                id='city'
                            />

                            <InfoTwoLinesBlock
                                title={translate('site.form.postalCode')}
                                subtitle={site?.postalCode ?? ''}
                                id='postalCode'
                            />

                            <InfoTwoLinesBlock
                                title={translate('site.form.country')}
                                subtitle={site?.country ?? ''}
                                id='country'
                            />

                            <InfoTwoLinesBlock
                                title={translate('place.installation_date')}
                                subtitle={avatar?.creationDate ? toShortFormatDate(avatar.creationDate) : ''}
                                id='installation_date'
                            />

                            <InfoTwoLinesBlock
                                title={translate('place.contact_info')}
                                subtitle={[
                                    (getAvatarPropertyValue(properties, CONTACT_NAME_AVATAR_PROPERTY) as string) ?? '',
                                    (getAvatarPropertyValue(properties, CONTACT_PHONE_AVATAR_PROPERTY) as string) ?? '',
                                    (getAvatarPropertyValue(properties, CONTACT_EMAIL_AVATAR_PROPERTY) as string) ?? '',
                                ]}
                                id='contact_info'
                            />
                        </Box>
                    </CollapseMenu>

                    <CollapseMenu icon={<DashboardOutlined />} text={translate('map.dialog.dashboard_information')}>
                        <Box
                            sx={{
                                p: 1,
                            }}
                        >
                            {avatar && (
                                <DashboardsInfoBox
                                    avatar={avatar}
                                    params={{
                                        place_id: avatarId,
                                    }}
                                    clientOrSite='place'
                                />
                            )}
                        </Box>
                    </CollapseMenu>
                </Box>
            )}
        </DialogTemplate>
    );
};

export default AvatarInfoDialog;
